import { useEffect, useState, useMemo } from "react";
import { useAtom } from "jotai";
import { useController } from "react-hook-form";
import debounce from "lodash.debounce";
import { useTransferPricing } from "@design-lab/entities/transfers-lab/api";
import type { Lens } from "@hookform/lenses";
import type { CustomSize } from "../model/transferLabTypes";
import { transferPricingAtom, pricingLoadingAtom, createPricingKey } from "../model/pricingAtoms";

interface UseCustomSizePricingParams {
  artworkId: string;
  transferTypeId: string;
  sizeLens: Lens<CustomSize>;
  enhancementTier: string;
  enabled?: boolean;
}

/**
 * Hook that automatically fetches pricing for a custom size when dimensions or quantity change
 * Stores pricing in Jotai atoms instead of form state
 */
export function useCustomSizePricing({
  artworkId,
  transferTypeId,
  sizeLens,
  enhancementTier,
  enabled = true
}: UseCustomSizePricingParams) {
  // Get individual field values through focused lenses
  const { field: widthField } = useController(sizeLens.focus("width").interop());
  const { field: heightField } = useController(sizeLens.focus("height").interop());
  const { field: quantityField } = useController(sizeLens.focus("quantity").interop());
  const { field: idField } = useController(sizeLens.focus("id").interop());

  // Extract the current values
  const sizeId = idField.value ?? "";
  const currentWidth = widthField.value ?? 0;
  const currentHeight = heightField.value ?? 0;
  const currentQuantity = quantityField.value ?? 0;

  // State for debounced values
  const [debouncedValues, setDebouncedValues] = useState({
    width: currentWidth,
    height: currentHeight,
    quantity: currentQuantity
  });

  // Track if we're waiting for debounce
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Create debounced update function
  const debouncedUpdate = useMemo(
    () =>
      debounce((width: number, height: number, quantity: number) => {
        setDebouncedValues({ width, height, quantity });
        setIsDebouncing(false);
      }, 500),
    []
  );

  // Update debounced values when current values change
  useEffect(() => {
    if (
      currentWidth !== debouncedValues.width ||
      currentHeight !== debouncedValues.height ||
      currentQuantity !== debouncedValues.quantity
    ) {
      setIsDebouncing(true);
      debouncedUpdate(currentWidth, currentHeight, currentQuantity);
    }
  }, [currentWidth, currentHeight, currentQuantity, debouncedValues, debouncedUpdate]);

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  // Get the main atoms
  const [allPricing, setAllPricing] = useAtom(transferPricingAtom);
  const [, setAllLoading] = useAtom(pricingLoadingAtom);

  // Create stable key for this size
  const pricingKey = createPricingKey(artworkId, transferTypeId, sizeId);

  // Create a unique ID for this specific custom size pricing
  // Remove hyphens to match backend's expected format
  const pricingId = `${artworkId}${sizeId}`.replace(/-/g, "");

  // Fetch pricing when debounced values change
  const {
    data: pricing,
    isLoading: isFetching,
    error
  } = useTransferPricing({
    transferTypeId,
    artworkId: pricingId,
    width: debouncedValues.width,
    height: debouncedValues.height,
    quantity: debouncedValues.quantity,
    enhancementTier,
    hasExpertEditAddon: false,
    enabled:
      enabled &&
      !!transferTypeId &&
      debouncedValues.width > 0 &&
      debouncedValues.height > 0 &&
      debouncedValues.quantity > 0
  });

  // Show loading state during debouncing or fetching
  const isLoading = isDebouncing || isFetching;

  // Update pricing atom when pricing changes
  useEffect(() => {
    if (pricing) {
      setAllPricing(prev => ({
        ...prev,
        [pricingKey]: pricing
      }));
    }
  }, [pricing, pricingKey, setAllPricing]);

  // Update loading state
  useEffect(() => {
    setAllLoading(prev => ({
      ...prev,
      [pricingKey]: isLoading
    }));

    return () => {
      setAllLoading(prev => {
        if (!(pricingKey in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[pricingKey];
        return next;
      });
    };
  }, [isLoading, pricingKey, setAllLoading]);

  // Return fresh data if available, otherwise stored data
  // This ensures we show the pricing immediately when it arrives
  const currentPricing = pricing || allPricing[pricingKey] || null;

  return {
    pricing: currentPricing,
    isLoading,
    error
  };
}
